/**
 * GuestZipPrompt — location modal (ZIP + radius) for Deals. Auto-opens once
 * for guests; opened on demand via "Change Location" for everyone.
 */
import React, { useEffect, useState } from "react";
import { MapPin, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { RADIUS_OPTIONS, DEFAULT_RADIUS } from "../hooks/useDealLocation";

export default function GuestZipPrompt({ open, initialZip = "", initialRadius = DEFAULT_RADIUS, isGuest = true, onClose, onSave }) {
  const [value, setValue] = useState(initialZip);
  const [radius, setRadius] = useState(initialRadius);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setValue(initialZip); setRadius(initialRadius || DEFAULT_RADIUS); setErr(""); setSaving(false); }
  }, [open, initialZip, initialRadius]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const clean = (value || "").trim().replace(/[^0-9]/g, "");
    if (clean.length !== 5) {
      setErr("Please enter a 5-digit US ZIP code.");
      return;
    }
    setSaving(true);
    const ok = await onSave(clean, radius);
    setSaving(false);
    if (ok === false) setErr("Couldn't save your location. Please try again.");
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[65] flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="guest-zip-overlay"
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-sm shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="guest-zip-modal"
      >
        <div className="relative bg-gradient-to-br from-emerald-500 to-green-600 p-5 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-2 right-2 p-1.5 hover:bg-white/15 rounded-full"
            aria-label="Close"
            data-testid="guest-zip-close"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            <h3 className="text-lg font-bold">Show me local deals</h3>
          </div>
          <p className="text-xs text-emerald-50 mt-1">
            Enter your ZIP and how far you&apos;ll travel. We only show stores within that distance.
          </p>
        </div>
        <form onSubmit={submit} noValidate className="p-5 space-y-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">ZIP code</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              autoFocus
              value={value}
              onChange={(e) => { setValue(e.target.value); setErr(""); }}
              placeholder="e.g. 43620"
              className="mt-1 w-full text-lg font-mono border border-gray-300 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="guest-zip-input"
            />
          </label>
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Search radius</span>
            <div className="mt-1.5 grid grid-cols-4 gap-1.5" role="radiogroup" data-testid="guest-radius-options">
              {RADIUS_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={radius === r}
                  onClick={() => setRadius(r)}
                  className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${
                    radius === r
                      ? "bg-emerald-600 border-emerald-600 text-white"
                      : "bg-white border-gray-300 text-gray-700 hover:border-emerald-400"
                  }`}
                  data-testid={`guest-radius-${r}`}
                >
                  {r} mi
                </button>
              ))}
            </div>
          </div>
          {err && <p className="text-xs text-red-600" data-testid="guest-zip-error">{err}</p>}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-md text-sm font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              data-testid="guest-zip-skip"
            >
              {isGuest ? "Skip" : "Cancel"}
            </button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-sm font-semibold"
              data-testid="guest-zip-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save location"}
            </Button>
          </div>
          <p className="text-[10px] text-gray-400 text-center">
            {isGuest ? "Stored on this device only. Never shared." : "Saved to your account. Used across Deals, Budget Friendly & List Saver."}
          </p>
        </form>
      </div>
    </div>
  );
}
